// Templates de checklist por tipo de produto.
// Cada tipo define: ordem de etapas + itens por etapa.
// Quando todos itens da etapa atual estão ticados, demanda avança pra próxima etapa do template.
//
// Item pode ser string simples (compat) OU objeto { titulo, papeisSugeridos } onde
// papeisSugeridos lista os papéis (Pessoa.papel ou cargo livre) que costumam executar.
// O coordenador atribui a(s) pessoa(s) responsável(eis) no momento da criação/edição.

import type { StatusDemanda } from './types';

// Taxonomia v2 (material → área) vive em materialAreaMap.ts. `TipoProduto`
// abaixo continua sendo a chave do engine de checklist (retrocompat com as
// demandas já cadastradas); o auto-roteamento por material é feito via
// MATERIAL_AREA_MAP no EditDemandaModal (S2).
export type { TipoMaterial, GrupoMaterial } from './materialAreaMap';

export type TipoProduto =
| 'Matéria de TV'
| 'Cliente Digital'
| 'Evento inter time'
| 'Conteúdo editorial'
| 'Vídeo institucional'
| 'Foto / Galeria'
| 'Post de rede social'
| 'Pesquisa'
| 'Campanha'
| 'Cobertura de evento'
| 'Vídeo para evento'
| 'Outro';

// Sugestão de papel (string livre — pode ser Pessoa.papel ou label de cargo informal)
export type PapelSugerido =
| 'Editor-chefe' | 'Repórter/Pauteira' | 'Pauteira' | 'Repórter'
| 'Videomaker' | 'Cinegrafista' | 'Editor de vídeo' | 'Editor'
| 'Designer' | 'Comercial' | 'Coordenador' | 'Gerente' | 'Analista'
| 'Subcoordenador' | 'Diretor';

export interface ChecklistItem {
/** ID estável (slug) — usado pra persistir progresso e responsáveis */
id: string;
titulo: string;
/** Sugestão de papéis que costumam executar esse passo. Coordenador escolhe pessoa(s) real(is). */
papeisSugeridos?: PapelSugerido[];
}

export interface ChecklistTemplate {
etapas: { status: StatusDemanda; itens: ChecklistItem[] }[];
}

/** Slugify helper */
function slug(s: string): string {
return s.toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 60);
}

function it(titulo: string, papeisSugeridos?: PapelSugerido[]): ChecklistItem {
return { id: slug(titulo), titulo, papeisSugeridos };
}

// Fluxo básico (decisão 08/jul/2026): tipos sem esteira própria seguem
// Briefing → Em produção → Revisão → [Aguardando aprovação, se exigida] → Finalizada,
// sem checklist pré-definido — o progresso é pelos status.
const ETAPAS_BASICAS: ChecklistTemplate['etapas'] = [
  { status: 'Triagem', itens: [] },
  { status: 'Em produção', itens: [] },
  { status: 'Em revisão', itens: [] },
  { status: 'Aguardando aprovação', itens: [] },
  { status: 'Publicada', itens: [] },
];

export const CHECKLIST_TEMPLATES: Record<TipoProduto, ChecklistTemplate> = {
// Modelo de referência — Matéria de TV (10 etapas: 2 briefing + 8 produção)
// Fluxo interno: editora-chefe valida e finaliza. Não passa por revisão externa/aprovação cliente.
'Matéria de TV': {
  etapas: [
    {
      status: 'Triagem',
      itens: [
        it('Conversa com cliente para definição de mote da matéria', ['Editor-chefe', 'Comercial', 'Repórter/Pauteira']),
        it('Definição dos personagens / fontes que vão ser entrevistados', ['Editor-chefe', 'Repórter/Pauteira']),
      ],
    },
    {
      status: 'Em produção',
      itens: [
        it('Bater pauta com dados para a matéria, naquele mote', ['Repórter/Pauteira']),
        it('Agendamento para captação', ['Repórter/Pauteira']),
        it('Captação (sonoras + imagens)', ['Repórter/Pauteira', 'Editor-chefe', 'Videomaker']),
        it('Logagem de material no servidor de arquivos', ['Videomaker']),
        it('Roteirização da matéria', ['Repórter/Pauteira', 'Editor-chefe']),
        it('Edição da matéria', ['Editor de vídeo', 'Editor', 'Editor-chefe', 'Repórter/Pauteira']),
        it('Revisão da matéria', ['Editor de vídeo', 'Editor', 'Editor-chefe']),
        it('Montagem da matéria no programa', ['Editor de vídeo', 'Editor']),
      ],
    },
    // Passa direto pra Publicada (sem revisão externa / aprovação de cliente)
    { status: 'Publicada', itens: [] },
  ],
},

// Cliente Digital — decisão 08/jul/2026: segue fluxo básico.
'Cliente Digital': { etapas: ETAPAS_BASICAS },

'Evento inter time': {
  etapas: [
    { status: 'Triagem', itens: [
      it('Briefing do evento', ['Coordenador']),
      it('Definição da grade / cerimonial', ['Coordenador']),
      it('Definição de patrocinadores e convidados', ['Coordenador', 'Comercial']),
    ] },
    { status: 'Em produção', itens: [
      it('Identidade visual (Branding)', ['Designer']),
      it('Materiais editoriais (Conteúdo)', ['Repórter/Pauteira']),
      it('Telas e vídeos (Produtora)', ['Editor de vídeo', 'Editor', 'Videomaker']),
      it('Apresentações (Dados)', ['Analista']),
      it('Logística (Eventos)', ['Coordenador']),
    ] },
    { status: 'Em revisão', itens: [
      it('Validação cruzada entre times', ['Coordenador']),
      it('Revisão final do material digital', ['Coordenador']),
    ] },
    { status: 'Publicada', itens: [] },
  ],
},

// Conteúdo editorial — decisão 08/jul/2026: segue fluxo básico.
'Conteúdo editorial': { etapas: ETAPAS_BASICAS },

// Vídeo institucional — decisão 08/jul/2026: segue fluxo básico.
'Vídeo institucional': { etapas: ETAPAS_BASICAS },

'Foto / Galeria': {
  etapas: [
    { status: 'Triagem', itens: [it('Briefing da cobertura', ['Coordenador']), it('Lista de cenas / pessoas a registrar', ['Coordenador'])] },
    { status: 'Em produção', itens: [it('Captação', ['Videomaker']), it('Seleção e tratamento', ['Designer'])] },
    { status: 'Em revisão', itens: [it('Revisão de qualidade', ['Coordenador'])] },
    { status: 'Aguardando aprovação', itens: [it('Cliente aprova galeria')] },
    { status: 'Publicada', itens: [] },
  ],
},

'Post de rede social': {
  etapas: [
    { status: 'Triagem', itens: [it('Briefing do post', ['Coordenador']), it('Definição da arte / texto', ['Designer', 'Repórter/Pauteira'])] },
    { status: 'Em produção', itens: [it('Diagramação', ['Designer']), it('Copy final', ['Repórter/Pauteira'])] },
    { status: 'Em revisão', itens: [it('Revisão editorial', ['Editor-chefe'])] },
    { status: 'Aguardando aprovação', itens: [it('Cliente aprova')] },
    { status: 'Publicada', itens: [] },
  ],
},

// Pesquisa — decisão 08/jul/2026: segue fluxo básico.
'Pesquisa': { etapas: ETAPAS_BASICAS },

// Campanha — decisão 08/jul/2026: segue fluxo básico (sem checklist pré-definido).
'Campanha': { etapas: ETAPAS_BASICAS },

// ——— Cobertura de evento (real time) — decisão 08/jul/2026 ———
// Orientado a CHECKLIST, não a esteira: a lista de entregas (N stories, posts
// LinkedIn, trechos de painelistas...) varia por evento e vive no checklist
// customizado do card. Conteúdo planeja/posta; Produtora capta/edita ao vivo.
// Sem aprovação de cliente; executa DURANTE o evento (sem SLA por etapa).
// Absorve os antigos "Post de rede social" e "Social ao vivo".
'Cobertura de evento': {
  etapas: [
    { status: 'Triagem', itens: [
      it('Montar lista de postagens (stories, LinkedIn, trechos de painelistas)', ['Repórter/Pauteira', 'Coordenador']),
      it('Definir equipe e frentes de captação (íntegra, apoio, celular)', ['Coordenador']),
      it('Levantar marcações de pessoas/empresas (info do Comercial via Eventos)', ['Comercial']),
    ] },
    { status: 'Em produção', itens: [
      it('Captação em tempo real no evento', ['Videomaker', 'Cinegrafista']),
      it('Edição real time das peças da lista', ['Editor de vídeo', 'Editor']),
      it('Publicação das peças (Conteúdo)', ['Repórter/Pauteira']),
    ] },
    { status: 'Publicada', itens: [] },
  ],
},

// ——— Vídeo para evento (sob demanda) — decisão 08/jul/2026 ———
// 2 aprovações internas (roteiro e peça final) por Comercial/Diretoria; o
// "cliente" é a própria Apex. Depois de aprovado, o Time Eventos exibe no evento.
// SLA: solicitar com no mínimo 10 dias de antecedência ao evento (D-10).
'Vídeo para evento': {
  etapas: [
    { status: 'Triagem', itens: [
      it('Solicitação registrada (Comercial/Diretoria)', ['Comercial', 'Diretor']),
      it('Roteiro', ['Repórter/Pauteira']),
      it('Roteiro aprovado por Comercial/Diretoria', ['Comercial', 'Diretor']),
    ] },
    { status: 'Em produção', itens: [
      it('Edição (Produtora)', ['Editor de vídeo', 'Editor']),
      it('Ajustes solicitados', ['Editor de vídeo', 'Editor']),
    ] },
    { status: 'Em revisão', itens: [it('Revisão do Conteúdo', ['Coordenador'])] },
    { status: 'Aguardando aprovação', itens: [it('Aprovação final Comercial/Diretoria', ['Comercial', 'Diretor'])] },
    { status: 'Publicada', itens: [] },
  ],
},

'Outro': {
  etapas: [
    { status: 'Triagem', itens: [] },
    { status: 'Em produção', itens: [] },
    { status: 'Em revisão', itens: [] },
    { status: 'Aguardando aprovação', itens: [] },
    { status: 'Publicada', itens: [] },
  ],
},
};

// ————— Sub-etapas por tipo (card inteligente) —————
// O single-select "Sub-etapa atual" do Airtable contém hoje apenas o conjunto de
// matéria/reel — ele NÃO vale para todos os tipos. Diretriz: cada tipo segue o
// fluxo do seu próprio template; tipo sem sub-etapas definidas segue o fluxo
// básico (o status é a etapa: Briefing → Em produção → Revisão → Finalizada).
// Para dar sub-etapas a um novo tipo: definir com o time e criar as opções no
// single-select do Airtable antes de listar aqui.
export interface SubEtapaGrupo { grupo: string; opcoes: string[] }
export const SUB_ETAPAS_POR_TIPO: Partial<Record<TipoProduto, SubEtapaGrupo[]>> = {
  'Matéria de TV': [
    { grupo: 'Briefing', opcoes: ['Briefing inicial', 'Pauta', 'Pernas / marcação'] },
    { grupo: 'Produção', opcoes: ['Agendamento', 'Captação', 'Roteiro', 'Edição'] },
    { grupo: 'Revisão', opcoes: ['Revisão técnica', 'Revisão editorial'] },
    { grupo: 'Final', opcoes: ['Cliente aprovando', 'Ajustes finais', 'Publicado'] },
  ],
};

// ————— Estrutura de tipos (decisão Renan 08/jul/2026, com base nas respostas dos times) —————
// Tipos que seguem o fluxo básico — recebem relabel Briefing/Revisão/Finalizada no card.
export const TIPOS_FLUXO_BASICO: TipoProduto[] = [
  'Cliente Digital', 'Conteúdo editorial', 'Vídeo institucional', 'Pesquisa', 'Campanha', 'Outro',
];
// Aposentados: não aparecem na criação/edição; cards antigos seguem funcionando.
// 'Evento inter time' → toda demanda tem cliente (mesmo interno);
// 'Foto / Galeria' → vira checklist do evento + campo "URL da galeria pós-evento";
// 'Post de rede social' → absorvido por 'Cobertura de evento'.
export const TIPOS_APOSENTADOS: TipoProduto[] = [
  'Evento inter time', 'Foto / Galeria', 'Post de rede social',
];

// ===== Helpers =====

export interface AvancoResult {
proximoStatus: StatusDemanda | null;
motivo: string;
}

/** Progresso: { etapa: { itemId: bool } } */
export type ProgressoMap = Record<string, Record<string, boolean>>;

export type ResponsaveisMap = Record<string, string[]>;

export function parseProgresso(json: string | null | undefined): ProgressoMap {
  if (!json) return {};
  try { return JSON.parse(json) as ProgressoMap; }
  catch { return {}; }
}

export function parseResponsaveis(json: string | null | undefined): ResponsaveisMap {
  if (!json) return {};
  try { return JSON.parse(json) as ResponsaveisMap; }
  catch { return {}; }
}

export function checarAvanco(tipoProduto: TipoProduto, statusAtual: StatusDemanda, progresso: ProgressoMap): AvancoResult {
  const template = CHECKLIST_TEMPLATES[tipoProduto];
  if (!template) return { proximoStatus: null, motivo: 'Tipo sem template' };
  const etapa = template.etapas.find(e => e.status === statusAtual);
  if (!etapa) return { proximoStatus: null, motivo: 'Etapa não encontrada' };
  const prog = progresso[statusAtual] ?? {};
  // Etapa sem checklist NÃO avança sozinha (senão todo salvamento pula coluna)
  if (etapa.itens.length === 0) return { proximoStatus: null, motivo: 'Etapa sem checklist' };
  const ok = etapa.itens.every(i => prog[i.id]);
  if (!ok) return { proximoStatus: null, motivo: 'Itens pendentes' };
  const idx = template.etapas.findIndex(e => e.status === statusAtual);
  const prox = template.etapas[idx + 1];
  return prox ? { proximoStatus: prox.status, motivo: 'Concluído' } : { proximoStatus: null, motivo: 'Última etapa' };
}

export function itensDaEtapa(tipoProduto: TipoProduto, status: StatusDemanda): ChecklistItem[] {
  return CHECKLIST_TEMPLATES[tipoProduto]?.etapas.find(e => e.status === status)?.itens ?? [];
}
